import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { MAIN_SITE_URL } from "@/data/categories";
import { articles } from "@/data/articles";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import JsonLd from "@/components/feature/JsonLd";
import { buildArticleSchema } from "@/lib/seo";

const sections = [
  { id: "why-it-matters", titleKey: "s1Title", bodyKey: "s1Body" },
  { id: "how-to-start", titleKey: "s2Title", bodyKey: "s2Body" },
  { id: "keep-in-mind", titleKey: "s3Title", bodyKey: "s3Body" },
];

export default function PillarArticle() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    document.title = article ? t(`articles.list.${slug}.title`) : t("brand");
    document.documentElement.lang = i18n.language;
  }, [i18n.language, slug, t, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-900">
        <SiteNav />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
          <h1 className="text-2xl font-bold text-foreground-950">{t("articles.notFound")}</h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("articles.backHome")}
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const articleSchema = buildArticleSchema({
    title: t(`articles.list.${slug}.title`),
    description: t(`articles.list.${slug}.excerpt`),
    path: `/articles/${slug}`,
    image: article.image,
  });

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <JsonLd data={articleSchema} />
      <SiteNav />

      <main>
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-16">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              {t("articles.breadcrumbHome")}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span>{t("articles.breadcrumbGuides")}</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">{t(`articles.list.${slug}.tag`)}</span>
          </nav>

          <h1 className="mt-6 text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">{t(`articles.list.${slug}.title`)}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {t("articles.author")}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {t("articles.readingTime")}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line" />
              {t("articles.lastUpdated")}
            </span>
          </div>

          <div className="mt-8 overflow-hidden rounded-xl border border-background-200">
            <img src={article.image} alt={t(`articles.list.${slug}.title`)} className="h-64 w-full object-cover object-top md:h-80" />
          </div>

          <p className="mt-8 text-lg leading-relaxed text-foreground-700">{t(`articles.list.${slug}.excerpt`)}</p>

          <div className="mt-8 rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("articles.tocTitle")}</h2>
            <ol className="mt-3 grid gap-2 sm:grid-cols-2">
              {sections.map((s, i) => (
                <li key={s.id} className="flex items-start gap-2">
                  <span className="mt-0.5 font-heading text-sm font-semibold text-accent-600">{i + 1}.</span>
                  <a href={`#${s.id}`} className="text-sm text-foreground-700 transition-colors hover:text-primary-600">
                    {t(`articles.${s.titleKey}`)}
                  </a>
                </li>
              ))}
              <li className="flex items-start gap-2">
                <span className="mt-0.5 font-heading text-sm font-semibold text-accent-600">{sections.length + 1}.</span>
                <a href="#faq" className="text-sm text-foreground-700 transition-colors hover:text-primary-600">
                  {t("articles.faqTitle")}
                </a>
              </li>
            </ol>
          </div>

          <div className="mt-10 space-y-10">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{t(`articles.${s.titleKey}`)}</h2>
                <p className="mt-3 text-base leading-relaxed text-foreground-700">{t(`articles.${s.bodyKey}`)}</p>
              </section>
            ))}

            <section id="faq" className="scroll-mt-24">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{t("articles.faqTitle")}</h2>
              <div className="mt-4 space-y-3">
                {([0, 1, 2] as const).map((i) => {
                  const isOpen = openFaq === i;
                  return (
                    <div key={i} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : i)}
                        className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left"
                      >
                        <span className="font-heading text-base font-semibold text-foreground-900">{t(`articles.faq.${i}.q`)}</span>
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-transform ${
                            isOpen ? "rotate-45" : ""
                          }`}
                        >
                          <i className="ri-add-line" />
                        </span>
                      </button>
                      {isOpen && <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{t(`articles.faq.${i}.a`)}</p>}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">{t("articles.relatedTitle")}</h2>
              <p className="mt-2 text-sm text-foreground-600">{t("articles.relatedIntro")}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-300 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-800 transition-colors hover:bg-primary-100"
                >
                  <i className="ri-home-4-line" />
                  {t("articles.relatedHome")}
                </Link>
                <Link
                  to="/usfans-spreadsheet-guide"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-book-read-line" />
                  {t("articles.relatedGuide")}
                </Link>
              </div>
            </section>

            <section className="rounded-2xl border border-primary-200 bg-primary-50 p-6 text-center md:p-8">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{t("articles.conclusionTitle")}</h2>
              <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-foreground-700">{t("articles.conclusionBody")}</p>
              <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  {t("articles.cta")}
                  <i className="ri-arrow-right-up-line text-base" />
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-foreground-200 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
                >
                  {t("articles.backHome")}
                </Link>
              </div>
            </section>

            <p className="text-xs leading-relaxed text-foreground-400">{t("articles.disclaimer")}</p>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}